import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { CloseOutlined, MenuOutlined } from '@ant-design/icons'
import { Button, Col, Layout, Row, Space, Spin, Typography } from 'antd'

import { authenticatedCustomerState } from 'src/data/customers/CustomersAtoms'
import { CustomerModel } from 'src/data/customers/type'
import HelpSection from 'src/modules/layouts/HelpSection'
import Navbar from 'src/modules/layouts/Navbar'
import NavbarExternal from 'src/modules/layouts/NavbarExternal'
import BonLookLogo from 'src/modules/shared/BonLookLogo'
import { logout, shopifyHomepage, shopifyHomepageStaging } from 'src/routes/NamedRoutes'
import { isMobile, isProduction } from 'src/utils/helper'

const { Header, Content, Sider } = Layout

type HeaderSectionProps = {
  collapsed: boolean
  setCollapse: (value: boolean) => void
  customer: CustomerModel
}

const HeaderSection = ({
  collapsed,
  setCollapse,
  customer,
}: HeaderSectionProps): JSX.Element => {
  const { t } = useTranslation()
  return (
    <Header className="default-layout__header">
      <Row justify="start" align="middle" gutter={isMobile() ? 24 : 0}>
        {isMobile() && (
          <Col>
            <Button
              className="default-layout__mobile-btn"
              icon={<>{collapsed ? <MenuOutlined /> : <CloseOutlined />}</>}
              onClick={() => {
                setCollapse(!collapsed)
              }}
            />
          </Col>
        )}
        <Col span={8}>
          <Row>
            <Col className="default-layout__back default-layout__logout">
              <Typography.Link
                href={!isProduction() ? shopifyHomepageStaging(customer.language) :  shopifyHomepage(customer.country, customer.language)}
              >
                {t(
                  `common:cta.go_back_shopify_homeapge_${
                    !isProduction() ? 'staging' : (customer.is_from_canada ? 'ca' : 'intl')
                  }`
                )}
              </Typography.Link>
            </Col>
          </Row>
        </Col>
        <Col xs={12} sm={8} className="text-centered">
          <BonLookLogo />
        </Col>
      </Row>
      <Row className="default-layout__welcome">
        {((isMobile() && !collapsed) || !isMobile()) && (
          <Col span={24}>
            <Typography.Text className="font-lora--26">
              {t('common:text.welcome_back', { name: customer.first_name })}
            </Typography.Text>
          </Col>
        )}
        {!isMobile() && (
          <Col span={24}>
            <Typography.Text className="default-layout__logout">
              {t('common:text.need_change_account')}{' '}
              <Link to={logout}>{t('common:navigation.logout')}</Link>.
            </Typography.Text>
          </Col>
        )}
      </Row>
    </Header>
  )
}

type SiderSectionProps = {
  collapsed: boolean
}

const SiderSection = ({ collapsed }: SiderSectionProps): JSX.Element => {
  return (
    <Sider
      className={`default-layout__sider ${
        collapsed && isMobile() ? 'hide-element' : ''
      }`}
      collapsedWidth={0}
      breakpoint="xs"
      trigger={null}
      width={isMobile() ? '100%' : 350}
      collapsed={collapsed && isMobile()}
    >
      <Space direction="vertical" size="large">
        <Navbar />
        <NavbarExternal />
        <HelpSection />
      </Space>
    </Sider>
  )
}

const LayoutWithSections = (): JSX.Element => {
  const { t } = useTranslation()

  const [collapsed, setCollapse] = useState(true)
  const customer = useRecoilValue(authenticatedCustomerState)
  const location = useLocation()
  useEffect(() => {
    if (isMobile()) {
      setCollapse(true)
    }
  }, [location])

  return (
    <>
      <HeaderSection
        collapsed={collapsed}
        setCollapse={setCollapse}
        customer={customer}
      />
      <Layout className="default-layout__content" hasSider={true}>
        <SiderSection collapsed={collapsed} />
        <Content className={!collapsed && isMobile() ? 'hide-element' : ''}>
          <React.Suspense
            fallback={
              <Spin
                spinning={true}
                className="default-layout__spinner"
                tip={t('common:loading')}
              />
            }
          >
            <Outlet />
          </React.Suspense>
        </Content>
      </Layout>
    </>
  )
}

export default LayoutWithSections
